import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Alert,
  Snackbar,
  Autocomplete,
  Grid,
  Switch,
  FormControlLabel
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as ActiveIcon,
  Pause as InactiveIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import {
  fetchSymbolsForEvent,
  activateEvent,
  deactivateEvent,
  updateEventConfig,
  deleteAssociation,
  enableAllForEvent,
  disableAllForEvent,
  clearCurrentEvent,
  clearSuccessMessage,
  addAvailableSymbol
} from './customEventSymbolsSlice';
import { fetchEvent } from '../customEvents/customEventsSlice';
import { fetchMarketSymbols } from '../markets/marketsSlice';
import { getMyQuotas } from '../../api/quotas';
import { CustomEventMetadataChips, getCustomEventEvaluationNotice } from '../customEvents/customEventMetadata';

const CustomEventConfigurationPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { eventId } = useParams();

  const {
    currentEventSymbols,
    currentEventLoading,
    currentEventError,
    isActivating,
    isDeactivating,
    isUpdating,
    isDeleting,
    isBulkUpdating,
    successMessage,
    availableSymbols
  } = useSelector((state) => state.customEventSymbols);

  const { currentEvent } = useSelector((state) => state.customEvents);

  const { symbols: marketSymbols, symbolsLoading } = useSelector((state) => state.markets);

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [selectedAssociation, setSelectedAssociation] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [currentPlanTier, setCurrentPlanTier] = useState(null);

  const defaultConfig = {
    enabled: true,
    maxSignalsPerDay: 5,
    timeframe: '1d'
  };

  const [config, setConfig] = useState(defaultConfig);

  useEffect(() => {
    if (eventId) {
      dispatch(fetchEvent(eventId));
      dispatch(fetchSymbolsForEvent(eventId));
    }

    return () => {
      dispatch(clearCurrentEvent());
    };
  }, [dispatch, eventId]);

  useEffect(() => {
    dispatch(fetchMarketSymbols());
  }, [dispatch]);

  useEffect(() => {
    getMyQuotas().then((data) => {
      setCurrentPlanTier(data?.tier || null);
    }).catch((error) => {
      console.error('Failed to fetch plan tier:', error);
    });
  }, []);

  useEffect(() => {
    if (successMessage) {
      setSnackbarOpen(true);
      setTimeout(() => {
        dispatch(clearSuccessMessage());
        setSnackbarOpen(false);
      }, 3000);
    }
  }, [successMessage, dispatch]);

  const handleBack = () => {
    navigate('/custom-events');
  };

  const handleAddSymbol = () => {
    setSelectedSymbol('');
    setConfig(defaultConfig);
    setAddDialogOpen(true);
  };

  const handleEditAssociation = (association) => {
    setSelectedAssociation(association);
    setConfig(association.config || defaultConfig);
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (association) => {
    setSelectedAssociation(association);
    setDeleteDialogOpen(true);
  };

  const handleToggleSymbol = async (symbol) => {
    if (!eventId || !symbol) return;

    const association = currentEventSymbols.find(
      (assoc) => assoc.symbol && assoc.symbol.toUpperCase() === symbol.toUpperCase()
    );

    if (association && association.isActive === 'true') {
      await dispatch(
        deactivateEvent({
          eventId: association.eventId,
          symbol: association.symbol
        })
      );
      return;
    }

    if (association) {
      await dispatch(
        activateEvent({
          eventId: association.eventId,
          symbol: association.symbol,
          config: association.config || defaultConfig
        })
      );
      return;
    }

    await dispatch(
      activateEvent({
        eventId,
        symbol,
        config: defaultConfig
      })
    );
  };

  const handleSubmitAdd = async () => {
    if (!selectedSymbol) return;

    await dispatch(activateEvent({
      eventId,
      symbol: selectedSymbol,
      config
    }));

    dispatch(addAvailableSymbol(selectedSymbol));

    setAddDialogOpen(false);
    setSelectedSymbol('');
  };

  const handleSubmitEdit = async () => {
    if (!selectedAssociation) return;

    await dispatch(updateEventConfig({
      eventId: selectedAssociation.eventId,
      symbol: selectedAssociation.symbol,
      config
    }));

    setEditDialogOpen(false);
    setSelectedAssociation(null);
  };

  const handleSubmitDelete = async () => {
    if (!selectedAssociation) return;

    await dispatch(deleteAssociation({
      eventId: selectedAssociation.eventId,
      symbol: selectedAssociation.symbol
    }));

    setDeleteDialogOpen(false);
    setSelectedAssociation(null);
  };

  const handleEnableAll = async () => {
    const symbols = combinedSymbols;
    await dispatch(enableAllForEvent({
      eventId,
      symbols,
      defaultConfig
    }));
  };

  const handleDisableAll = async () => {
    const symbols = currentEventSymbols.map((assoc) => assoc.symbol);
    await dispatch(disableAllForEvent({
      eventId,
      symbols
    }));
  };

  const isSymbolActive = (symbol) => {
    const association = currentEventSymbols.find(
      (assoc) => assoc.symbol && assoc.symbol.toUpperCase() === symbol.toUpperCase()
    );
    return association?.isActive === 'true';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString();
  };

  const combinedSymbols = (() => {
    const symbolSet = new Set();
    (marketSymbols || []).forEach((symbol) => symbolSet.add(symbol));
    availableSymbols.forEach((symbol) => symbolSet.add(symbol));
    currentEventSymbols.forEach((assoc) => {
      if (assoc.symbol) symbolSet.add(assoc.symbol);
    });
    return Array.from(symbolSet).sort();
  })();
  const evaluationNotice = getCustomEventEvaluationNotice(currentEvent, { currentPlanTier });

  if (currentEventLoading) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Typography variant="h6">Loading event configuration...</Typography>
      </Container>
    );
  }

  if (currentEventError) {
    return (
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Alert severity="error">{currentEventError}</Alert>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={handleBack} sx={{ mr: 2 }}>
          <BackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4">
            Configure Custom Event
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {currentEvent?.name || 'Custom Event'} • {currentEvent?.description || 'No description'}
          </Typography>
        </Box>
      </Box>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Saved event metadata
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Review the persisted execution tier and compatibility before configuring symbol-specific activation.
          </Typography>
          <CustomEventMetadataChips
            customEvent={currentEvent}
            currentPlanTier={currentPlanTier}
            showHelperText
          />
          {evaluationNotice && (
            <Alert severity={evaluationNotice.severity} sx={{ mt: 2 }}>
              <strong>{evaluationNotice.title}:</strong> {evaluationNotice.message}
              {evaluationNotice.detail ? ` ${evaluationNotice.detail}` : ''}
            </Alert>
          )}
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Typography variant="h6" gutterBottom>
                Symbol Activation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Enable or disable this event per symbol, or apply bulk actions.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }} sx={{ textAlign: { xs: 'left', md: 'right' } }}>
              <Button
                variant="outlined"
                onClick={handleEnableAll}
                disabled={isBulkUpdating}
                sx={{ mr: 1 }}
              >
                Enable All
              </Button>
              <Button
                variant="outlined"
                onClick={handleDisableAll}
                disabled={isBulkUpdating}
              >
                Disable All
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Symbols</Typography>
            <Button startIcon={<AddIcon />} onClick={handleAddSymbol}>
              Add Symbol
            </Button>
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {combinedSymbols.map((symbol) => (
              <Chip
                key={symbol}
                label={symbol}
                color={isSymbolActive(symbol) ? 'success' : 'default'}
                onClick={() => handleToggleSymbol(symbol)}
                icon={isSymbolActive(symbol) ? <ActiveIcon /> : <InactiveIcon />}
              />
            ))}
          </Box>
          {symbolsLoading && (
            <Typography variant="caption" color="text.secondary">
              Loading market symbols...
            </Typography>
          )}
        </Paper>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
      >
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Symbol</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Config</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {currentEventSymbols.map((association) => (
                <TableRow key={association.eventSymbol}>
                  <TableCell>{association.symbol}</TableCell>
                  <TableCell>
                    <Chip
                      label={association.isActive === 'true' ? 'Active' : 'Inactive'}
                      color={association.isActive === 'true' ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {association.config?.timeframe || '1d'} • {association.config?.maxSignalsPerDay || 5} signals/day
                    </Typography>
                  </TableCell>
                  <TableCell>{formatDate(association.updatedAt)}</TableCell>
                  <TableCell>
                    <IconButton
                      size="small"
                      onClick={() => handleEditAssociation(association)}
                      title="Edit"
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => handleDeleteClick(association)}
                      title="Delete"
                      color="error"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              {currentEventSymbols.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography variant="body2" color="text.secondary">
                      No symbols configured yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </motion.div>

      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)}>
        <DialogTitle>Add Symbol</DialogTitle>
        <DialogContent>
          {evaluationNotice && (
            <Alert severity={evaluationNotice.severity} sx={{ mt: 1, mb: 2 }}>
              <strong>{evaluationNotice.title}:</strong> {evaluationNotice.message}
              {evaluationNotice.detail ? ` ${evaluationNotice.detail}` : ''}
            </Alert>
          )}
          <Autocomplete
            value={selectedSymbol}
            onChange={(_, value) => setSelectedSymbol(value || '')}
            options={combinedSymbols}
            renderInput={(params) => (
              <TextField {...params} label="Symbol" margin="dense" />
            )}
            freeSolo
          />
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Max Signals Per Day"
              type="number"
              value={config.maxSignalsPerDay}
              onChange={(e) => setConfig({ ...config, maxSignalsPerDay: Number(e.target.value) })}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={config.timeframe}
                label="Timeframe"
                onChange={(e) => setConfig({ ...config, timeframe: e.target.value })}
              >
                <MenuItem value="1d">Daily</MenuItem>
                <MenuItem value="1w">Weekly</MenuItem>
                <MenuItem value="1h">Hourly</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitAdd} variant="contained" disabled={isActivating}>
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)}>
        <DialogTitle>Edit Configuration</DialogTitle>
        <DialogContent>
          {evaluationNotice && (
            <Alert severity={evaluationNotice.severity} sx={{ mt: 1, mb: 2 }}>
              <strong>{evaluationNotice.title}:</strong> {evaluationNotice.message}
              {evaluationNotice.detail ? ` ${evaluationNotice.detail}` : ''}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={config.enabled}
                  onChange={(e) => setConfig({ ...config, enabled: e.target.checked })}
                />
              }
              label="Enabled"
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Max Signals Per Day"
              type="number"
              value={config.maxSignalsPerDay}
              onChange={(e) => setConfig({ ...config, maxSignalsPerDay: Number(e.target.value) })}
            />
          </Box>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Timeframe</InputLabel>
              <Select
                value={config.timeframe}
                label="Timeframe"
                onChange={(e) => setConfig({ ...config, timeframe: e.target.value })}
              >
                <MenuItem value="1d">Daily</MenuItem>
                <MenuItem value="1w">Weekly</MenuItem>
                <MenuItem value="1h">Hourly</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitEdit} variant="contained" disabled={isUpdating}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Association</DialogTitle>
        <DialogContent>
          <Typography>
            Remove {selectedAssociation?.symbol} from this event?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSubmitDelete} color="error" variant="contained" disabled={isDeleting}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="success"
          onClose={() => setSnackbarOpen(false)}
        >
          {successMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CustomEventConfigurationPage;
